import React from 'react';
import PropTypes from 'prop-types';
import Editor, {composeDecorators} from 'draft-js-plugins-editor';
import { EditorState, RichUtils, convertToRaw, Modifier, convertFromRaw, AtomicBlockUtils, KeyBindingUtil, getDefaultKeyBinding } from 'draft-js';
import CssBaseline from '@material-ui/core/CssBaseline';

// Draft JS Plugins
import createImagePlugin from 'draft-js-image-plugin';
import createFocusPlugin from 'draft-js-focus-plugin';
import createResizeablePlugin from 'draft-js-resizeable-plugin';

// Components
import Header from '../components/Header/Header';
import InlineToolbar from '../components/InlineToolbar/InlineToolbar';
import StaticToolbar from '../components/StaticToolbar/StaticToolbar';
import AlignmentToolbar from '../components/AlignmentToolbar/AlignmentToolbar';

// Material UI Components
import { ThemeProvider, Container, Box } from '@material-ui/core';

// Style sheet
import { createMuiTheme } from '@material-ui/core/styles';
import 'draft-js/dist/Draft.css'
import 'draft-js-focus-plugin/lib/plugin.css';
import 'draft-js-image-plugin/lib/plugin.css';
import './App.css';

// Material UI Custom Theme
const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#2980B9',
    },
    secondary: {
      main: '#C0392B'
    },
    success: {
      main: '#27AE60',
    }
  },
});

// Draft JS Plugins Instance
const focusPlugin = createFocusPlugin();
const resizeablePlugin = createResizeablePlugin();

// Draft JS Plugins Decorator
const decorator = composeDecorators(
  resizeablePlugin.decorator,
  focusPlugin.decorator,
);

const imagePlugin = createImagePlugin({ decorator });

// Custom Plugin
const linkStrategy = (contentBlock, callback, contentState) => {
	contentBlock.findEntityRanges(character => {
		const entityKey = character.getEntity();
		return (
			entityKey !== null &&
			contentState.getEntity(entityKey).getType() === "LINK"
		);
	}, callback);
};

const Link = (props) => {
  const { contentState, entityKey } = props;
  const { url } = contentState.getEntity(entityKey).getData();
  return (
    <a
      className="link"
      href={url}
      rel="noopener noreferrer"
      target="_blank"
      aria-label={url}
    >{props.children}</a>
  );
};

const linkPlugin = {
  decorators: [
    {
      strategy: linkStrategy,
      component: Link,
    },
  ],
}

const plugins = [
  focusPlugin,
  resizeablePlugin,
  imagePlugin,
  linkPlugin
];

export default class App extends React.Component{
  constructor(props){
    super(props);

    this.state = {
      editMode: false,
      editorState: EditorState.createEmpty(),
      previousEditorState: null,
      fontSize: '14',
      fontColor: 'INHERIT',
      highlighterColor: 'TRANSPARENT',
      imageAlignmentType: null,
      currentNote: null,
      notes: [],
    }
  }

  static propTypes = {
    editMode: PropTypes.bool,
    fontSize: PropTypes.string,
    fontColor: PropTypes.string,
    highlighterColor: PropTypes.string,
    imageAlignmentType: PropTypes.string,
    currentNote: PropTypes.object,
    notes: PropTypes.array,
}

  componentDidMount(){
    // Will set a variables with the value of the local storage 
    const setNotes = JSON.parse(localStorage.getItem("Notes") || "[]");

    this.setState({
      notes: setNotes,
    })
  }

  // This method will handle the Editor whenever something changes
  handleEditorChange = (editorState) => {
    // console.log(convertToRaw(editorState.getCurrentContent()));

    this.setState({
      editorState: editorState,
    })
  };

  // This method will set the focus on the editor
  handleEditorFocus = () => {
    this.editor.focus();
  };

  // This method will handle whenever the user presses the tab key
  onEditorTab = (event) => {
    event.preventDefault();

    const tabCharacter = "    ";
    
    const editorState = this.state.editorState;
    const newContentState = Modifier.replaceText(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      tabCharacter
    );

    this.setState({ 
      editorState: EditorState.push(editorState, newContentState, 'insert-characters')
    });
  }

  // This method will set the editor to edit mode (Not read only) 
  handleClickEditMode = () => {
    this.handleCursorSetAtEnd(this.state.editorState);
    
    this.setState(state => ({
      editMode: !state.editMode,
    }))
  }

  // This method will toggle the a block style
  handleToggleBlockStyle = (block) => {
    this.handleEditorChange(RichUtils.toggleBlockType(this.state.editorState, block))
  }
  
  // This method will either toggle a style or remove it then apply the new one if the style is any of the values from the array
  handleToggleStyle = (style) => {
    const checkStylesArray = ['14', '16', '24', '32', '40', '64', '96', "INHERIT", "#FAFAFA", "#BDC3C7", "#C0392B", "#F1C40F", "#F39C12", "#8E44AD", "#2980B9", "#27AE60", "TRANSPARENT", "#34495E", "#95A5A6", "#E74C3C", "#E67E22","#9B59B6", "#3498DB", "#2ECC71", "#1ABC9C"];

    if(checkStylesArray.includes(style)){
      this.handleToggleRemoveStyle(style);
    }else{
      this.handleEditorChange(RichUtils.toggleInlineStyle(this.state.editorState, style))
    }
  }

  // This method will remove the current style from the selected text 
  handleToggleRemoveStyle = (style) => {
    const currentEditorState = this.state.editorState;
    const fontSize = ['14', '16', '24', '32', '40', '64', '96'];
    const fontColor = ["INHERIT", "#FAFAFA", "#BDC3C7", "#C0392B", "#F1C40F", "#F39C12", "#8E44AD", "#2980B9", "#27AE60"];
    const highlighterColor = ["TRANSPARENT", "#34495E", "#95A5A6", "#E74C3C", "#E67E22","#9B59B6", "#3498DB", "#2ECC71", "#1ABC9C"];
    let previousStyle;

    if(fontSize.includes(style)){
      previousStyle = this.state.fontSize;

      this.setState({
        fontSize: style
      })
    }else if(fontColor.includes(style)){
      previousStyle = this.state.fontColor;

      this.setState({
        fontColor: style
      })
    }else if(highlighterColor.includes(style)){
      previousStyle = this.state.highlighterColor;

      this.setState({
        highlighterColor: style
      })
    }

    const selectionState = currentEditorState.getSelection();
    const contentState = currentEditorState.getCurrentContent();

    const removePreviousStyle = Modifier.removeInlineStyle(
      contentState,
      selectionState,
      previousStyle
    );
      
    const removePreviousStyleBlock = removePreviousStyle.getBlockMap();
    
    const newInlineStyle = Modifier.mergeBlockData(removePreviousStyle, selectionState, removePreviousStyleBlock);
    const newEditorState = EditorState.push(currentEditorState, removePreviousStyle, newInlineStyle);

    this.handleToggleApplyStyle(newEditorState, style);
  }

  // This method will apply the style chosen to the selected text
  handleToggleApplyStyle = (editorState, style) => {
    const newEditorState = editorState;

    const selectionState = newEditorState.getSelection();
    const contentState = newEditorState.getCurrentContent();

    const applyNewStyle = Modifier.applyInlineStyle(
      contentState,
      selectionState,
      style
    );
      
    const applyNewStyleBlock = applyNewStyle.getBlockMap();
    
    const newInlineStyle = Modifier.mergeBlockData(applyNewStyle, selectionState, applyNewStyleBlock);
    const updateEditorState = EditorState.push(newEditorState, applyNewStyle, newInlineStyle);

    this.setState({
      editorState: updateEditorState,
    });
  }

  // This method will set the cursor at the end of the editor
  handleCursorSetAtEnd = (editorState) => {
    const currentEditorState = editorState === null ? this.state.editorState : editorState;

    this.setState({
      editorState: EditorState.moveFocusToEnd(currentEditorState)
    })
  }

  // This method will clear the editor
  handleClickClearEditor = () => {
    this.setState({
      previousEditorState: this.state.editorState,
    })

    const newEditorState = EditorState.createEmpty();

    const selectionState = newEditorState.getSelection();
    const contentState = newEditorState.getCurrentContent();

    const contentBlock = contentState.getBlockMap();
  
    const newBlock = Modifier.mergeBlockData(contentState, selectionState, contentBlock);
    const updateEditorState = EditorState.push(newEditorState, contentState, newBlock);

    this.setState({
      editorState: updateEditorState,
    })
  }

  // This method will set the editor back to view-mode and will not save the changes that the user has done.
  onClickCancelEditor = () => {
    this.setState(state => ({
      editMode: !state.editMode,
    }))
  }

  // This method will remove the last style inserted into the selected text
  handleClickRemoveLastFormat = (key) => {
    const editorState = this.state.editorState;
    const selectionState = editorState.getSelection();
  
    const contentState = editorState.getCurrentContent();

    const editorRaw = convertToRaw(editorState.getCurrentContent());
    const editorRawBlock = editorRaw.blocks.find(item => item.key === key);

    const inlineStyleArray = editorRawBlock.inlineStyleRanges;

    inlineStyleArray.forEach(item => {
      const removeStyle = Modifier.removeInlineStyle(
        contentState,
        selectionState,
        item.style
      );

      const removeStyleBlock = removeStyle.getBlockMap();
      
      const newInlineStyle = Modifier.setBlockData(removeStyle, selectionState, removeStyleBlock);

      const newEditorState = EditorState.push(editorState, removeStyle, newInlineStyle);

      this.setState({
        editorState: newEditorState,
      });
    })
  }
  
  // This method will either turn the text into a working link or turn off the link
  handleClickToggleLink = (type, url) => {
    const editorState = this.state.editorState;
    const selectionState = editorState.getSelection();
    const contentState = editorState.getCurrentContent();

    const contentStateWithEntity = contentState.createEntity("LINK", "MUTABLE", {url: url});

    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    
    const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
    
    if(type === "OFF"){
      this.handleEditorChange(RichUtils.toggleLink(newEditorState, selectionState, null))
    }else {
      this.handleEditorChange(RichUtils.toggleLink(newEditorState, selectionState, entityKey))
    }
  }

  // This method will set the url entered into an image component inside the editor
  handleClickToggleImageUrl = (url) => {
    const newEditorState = handleInsertImage(this.state.editorState, url);
    this.handleEditorChange(newEditorState)
  }

  // This method will set the alignment of the image
  handleClickSetImageAlignment = (type, block) => {
    this.setState({
      imageAlignmentType: type
    }, () => this.handleToggleBlockStyle(block))
  }

  // This method will save the editor and title and create with the data a new note
  handleClickSave = (titleParam) => {
    const date = new Date();
    const currentNotes = [...this.state.notes];
    const newCurrentNote = this.state.currentNote;

    const noteData = convertToRaw(this.state.editorState.getCurrentContent());

    const contentBlock = noteData.blocks[0];
    const contentText = contentBlock.text;
    const contentTextArray = contentText.split(" ");

    let title;
    let drawerTitle;
    
    if(titleParam === "New Note" || titleParam === "" ){
      title = contentTextArray[0];
      if(title === ""){
        title = drawerTitle = 'New Note';
      }else {
        const titleArray = title.split("");
        const newTitleArray = titleArray.slice(0, 12);
        const newTitle = newTitleArray.join("");
    
        title = `${title}...`;
        drawerTitle = `${newTitle}...`;
      }
    }else{
      if(titleParam.length > 12){
        const titleArray = titleParam.split("");
        const newTitleArray = titleArray.slice(0, 12);
        const newTitle = newTitleArray.join("");
    
        drawerTitle = `${newTitle}...`;
      }else{
        drawerTitle = titleParam;
      }
      title = titleParam;
    }

    const day = date.getDate()
    const month = (date.getMonth() + 1)
    const year = date.getFullYear()
    
    const fullDate = `${month}/${day}/${year}`;

    let newNote;
    let newNotes = [];

    if(newCurrentNote === null){
      newNote = {
        id: Math.random(),
        title: title,
        titleDrawer: drawerTitle,
        date: fullDate,
        password: "",
        locked: false,
        imageAlignmentType: this.state.imageAlignmentType,
        data: noteData
      }

      newNotes = [...currentNotes];
      newNotes.unshift(newNote);
    }else{
      newNote = {
        id: newCurrentNote.id,
        title: title,
        titleDrawer: drawerTitle,
        date: fullDate,
        password: newCurrentNote.password,
        locked: newCurrentNote.locked,
        imageAlignmentType: this.state.imageAlignmentType,
        data: noteData
      }

      const getNote = currentNotes.find(item => {
        return item.id === newNote.id
      })

      newNotes = currentNotes.filter(item => {
        return item.id !== getNote.id
      })

      newNotes.unshift(newNote);
    }
    
    // Setting the Alarms array into our local storage
    localStorage.setItem("Notes", JSON.stringify(newNotes));

    this.setState(state => ({
      currentNote: newNote,
      editMode: !state.editMode,
      notes: newNotes,
    }))
  }

  // This method will clear the notes
  handleClickClearNotes = () => {
    // Deleting the Notes item from local storage
    localStorage.removeItem("Notes")

    this.handleClickOpenNote("New")

    this.setState({
      notes: []
    })
  }

  // This method will either lock the note or unlock the note depending on its locked state
  handleClickToggleLock = (id, password, type) => {
    // Getting the current state of notes array
    const currentNotes = [...this.state.notes];
    
    const newNotes = currentNotes.map(item => {
      if(item.id === id){
        if(type === "turnOn"){
          item.password = password;
        }else{
          item.password = "";
        }
        item.locked = !item.locked;
      }
      return item
    })

    // Setting the Notes array into our local storage
    localStorage.setItem("Notes", JSON.stringify(newNotes));

    this.setState({
      notes: newNotes
    })
  }
  
  // This method will delete the note from the notes array
  handleClickDeleteNote = (id) => {
    const itemId = id;

    // Getting the current state of notes array
    const currentNotes = [...this.state.notes];

    // Remove the item from the new notes array
    const newNotes = currentNotes.filter(item => {
        return item.id !== itemId
    });

    // Setting the Notes array into our local storage
    localStorage.setItem("Notes", JSON.stringify(newNotes));

    this.handleClickOpenNote("New")

    this.setState({
        notes: newNotes,
    })
  }

  // This method will check whether the user wants to open a new note or a previously created note
  handleClickOpenNote = (type, note) => {
    if(type === "New"){
      this.setState({
        editorState: EditorState.createEmpty(),
        currentNote: null,
        imageAlignmentType: null,
      })
    }else{
      const data = convertFromRaw(note.data);

      this.setState({
        editorState: EditorState.createWithContent(data),
        currentNote: note,
        imageAlignmentType: note.imageAlignmentType,
      })
    }
  }

  // This method is to try remove dublicate code
  undoRedoMethod = (type) => {
    if(this.state.previousEditorState !== null){
      this.handleEditorChange(type(this.state.previousEditorState));

      this.setState({
        previousEditorState: null
      })
    }else{
      this.handleEditorChange(type(this.state.editorState));
    }
  }

  // This method will undo the last edit to the editor
  handleUndo = () => {
    this.undoRedoMethod(EditorState.undo);
  } 

  // This method will redo the last edit that was undone to the editor
  handleRedo = () => {
    this.undoRedoMethod(EditorState.redo);
  }

  // Custom block styles
  blockStyles = (contentBlock) => {
    const type = contentBlock.getType();
    const imageAlignmentType = this.state.imageAlignmentType === null || this.state.imageAlignmentType === undefined ? "" : this.state.imageAlignmentType;

    // Alignment
    if (type === 'ALIGNLEFT') {
      return 'alignLeft';
    } else if (type === 'ALIGNCENTERED') {
      return 'alignCenter';
    } else if (type === 'ALIGNRIGHT') {
      return 'alignRight';
    }

    // Image Alignment
    if (type === 'atomic'){
      if (imageAlignmentType === 'ALIGNIMGLEFT') {
        return 'alignImgLeft';
      } else if (imageAlignmentType === 'ALIGNIMGCENTERED') {
        return 'alignImgCenter';
      } else if (imageAlignmentType === 'ALIGNIMGRIGHT') {
        return 'alignImgRight';
      }
    }
  }

  // This method will return a string depending on the keys the user presses
  keyBindingFn = (event) => {
    if (KeyBindingUtil.hasCommandModifier(event) && event.keyCode === 66) { return 'BOLD'; }
    else if (KeyBindingUtil.hasCommandModifier(event) && event.keyCode === 73) { return 'ITALIC'; }
    else if (KeyBindingUtil.hasCommandModifier(event) && event.keyCode === 85) { return 'UNDERLINE'; }
    else if (KeyBindingUtil.hasCommandModifier(event) && event.keyCode === 83) { return 'SAVE'; }
    else if (KeyBindingUtil.hasCommandModifier(event) && event.shiftKey && event.keyCode === 90) { return 'REDO'; }
    else if (KeyBindingUtil.hasCommandModifier(event) && event.keyCode === 90) { return 'UNDO'; }

    return getDefaultKeyBinding(event);
  }

  // This method will execute the return string from the function keyBindingFn 
  handleKeyCommand = (command) => {
    switch(command){
      case "BOLD":
        this.handleEditorChange(RichUtils.toggleInlineStyle(this.state.editorState, command))
        return 'handled';
      case "ITALIC":
        this.handleEditorChange(RichUtils.toggleInlineStyle(this.state.editorState, command))
        return 'handled';
      case "UNDERLINE":
        this.handleEditorChange(RichUtils.toggleInlineStyle(this.state.editorState, command))
        return 'handled';
      case "SAVE":
        this.handleClickSave("")
        return 'handled';
      case "UNDO":
        this.handleUndo("")
        return 'handled';
      case "REDO":
        this.handleRedo()
        return 'handled';
      default:
        return 'not-handled';
    }
  }
  
  render(){
    return (
      <React.Fragment>
        <CssBaseline />
        <ThemeProvider theme={theme}>
          <Container className='Container' maxWidth='md' disableGutters>
            <Header 
              editMode={this.state.editMode}
              currentNote={this.state.currentNote}
              notes={this.state.notes}
              onClickSave={this.handleClickSave} 
              onClickEditMode={this.handleClickEditMode} 
              onClickCancelEditor={this.onClickCancelEditor}
              onClickClearNotes={this.handleClickClearNotes} 
              onClickToggleLock={this.handleClickToggleLock} 
              onClickDeleteNote={this.handleClickDeleteNote} 
              onClickOpenNote={this.handleClickOpenNote} 
              />
            <Box m={2}>
              <StaticToolbar 
                editorState={this.state.editorState} 
                editMode={this.state.editMode}
                onClickUndo={this.handleUndo}
                onClickRedo={this.handleRedo}
                onClickToggleImageUrl={this.handleClickToggleImageUrl}
                onClickClearEditor={this.handleClickClearEditor}
              />
              <div onClick={() => this.handleEditorFocus}>
                <Editor 
                  customStyleMap={inlineStyles}
                  blockStyleFn={this.blockStyles}
                  editorState={this.state.editorState}
                  onChange={this.handleEditorChange}
                  onFocus={this.handleEditorFocus}
                  onTab={this.onEditorTab}
                  plugins={plugins}
                  ref={(element) => { this.handleEditorFocus = element; }}
                  readOnly={!this.state.editMode}
                  handleKeyCommand={this.handleKeyCommand}
                  keyBindingFn={this.keyBindingFn}
                  spellCheck={true}
                  placeholder='Enter your thoughts...'
                />
                {
                  this.state.editMode === true
                    ? 
                    <AlignmentToolbar 
                      editorState={this.state.editorState} 
                      imageAlignmentType={this.state.imageAlignmentType} 
                      onClickSetImageAlignment={this.handleClickSetImageAlignment}
                      handleCursorSetAtEnd={this.handleCursorSetAtEnd}
                    />
                      : null
                }               
              </div>
              <InlineToolbar 
                editorState={this.state.editorState} 
                onClickToggleBlockStyle={this.handleToggleBlockStyle}
                onClickToggleStyle={this.handleToggleStyle}
                handleCursorSetAtEnd={this.handleCursorSetAtEnd}
                onClickRemoveLastFormat={this.handleClickRemoveLastFormat}
                onClickToggleLink={this.handleClickToggleLink}
              />
            </Box>
          </Container>
        </ThemeProvider>
      </React.Fragment>
    );
  }
}

// Custom inline Styles
const inlineStyles = {
  'UPPERCASE': {
    textTransform: 'uppercase',
  },
  '14': {
    fontSize: '14px',
  },
  '16': {
    fontSize: '16px',
  },
  '24': {
    fontSize: '24px',
  },
  '32': {
    fontSize: '32px',
  },
  '40': {
    fontSize: '40px',
  },
  '64': {
    fontSize: '64px',
  },
  '96': {
    fontSize: '96px',
  },
  'TRANSPARENT': {
    background: 'transparent',
  },
  '#34495E': {
    background: '#34495E'
  },
  '#95A5A6': {
    background: '#95A5A6'
  },
  '#E74C3C': {
    background: '#E74C3C'
  },
  '#E67E22': {
    background: '#E67E22'
  },
  '#9B59B6': {
    background: '#9B59B6'
  },
  '#3498DB': {
    background: '#3498DB'
  },
  '#2ECC71': {
    background: '#2ECC71'
  },
  '#1ABC9C': {
    background: '#1ABC9C'
  },
  'INHERIT': {
    color: 'inherit'
  },
  '#FAFAFA': {
    color: '#FAFAFA'
  },
  '#BDC3C7': {
    color: '#BDC3C7'
  },
  '#C0392B': {
    color: '#C0392B'
  },
  '#F1C40F': {
    color: '#F1C40F'
  },
  '#F39C12': {
    color: '#F39C12'
  },
  '#8E44AD': {
    color: '#8E44AD'
  },
  '#2980B9': {
    color: '#2980B9'
  },
  '#27AE60': {
    color: '#27AE60'
  }
};

// This method will insert a figure tag with the image the user provided by url
const handleInsertImage = (editorState, url) => {
  const contentState = editorState.getCurrentContent();
  const contentStateWithEntity = contentState.createEntity(
    "IMAGE",
    "IMMUTABLE",
    { src: url }
  );
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  const newEditorState = EditorState.set(editorState, {
    currentContent: contentStateWithEntity
  });
  return AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " ");
};